import React, { FC, useEffect, useState } from "react";
import { ESexo, IAvaliacaoDiariaQuestionario } from "@deltasge/marauders-map";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  GridSize,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  Magnify as ViewIcon,
  CheckBold as CheckIcon,
  Filter as FilterIcon,
} from "mdi-material-ui";
import { Avatar } from "components/Avatar";
import { useMobile } from "hooks/useMobile";
import { useAppSelector } from "store";

import childrenf01 from "assets/images/children-f-01.png";
import childrenf02 from "assets/images/children-f-02.png";
import childrenf03 from "assets/images/children-f-03.png";
import childrenf04 from "assets/images/children-f-04.png";

import childrenm01 from "assets/images/children-m-01.png";
import childrenm02 from "assets/images/children-m-02.png";
import childrenm03 from "assets/images/children-m-03.png";
import childrenm04 from "assets/images/children-m-04.png";

const childrensM = [childrenm01, childrenm02, childrenm03, childrenm04];
const childrensF = [childrenf01, childrenf02, childrenf03, childrenf04];

const getRandomChildren = (s: ESexo) => {
  let min = 0;
  let max = 3;
  min = Math.ceil(min);
  max = Math.floor(max);
  const choice = Math.floor(Math.random() * (max - min + 1) + min);
  if (s == ESexo.Feminino) return childrensF[choice];
  else return childrensM[choice];
};

export const CardAvaliacao: FC<{
  avaliacao: IAvaliacaoDiariaQuestionario;
  handleClick: (avaliacao: IAvaliacaoDiariaQuestionario) => void;
  handleDelete: (avaliacao: IAvaliacaoDiariaQuestionario) => void;
  handleVisualizar: (avaliacao: IAvaliacaoDiariaQuestionario) => void;
  handleAvaliacoes: (avaliacao: IAvaliacaoDiariaQuestionario) => void;
}> = ({
  avaliacao,
  handleClick,
  handleDelete,
  handleAvaliacoes,
  handleVisualizar,
}) => {
  const [progress, setProgress] = useState(0);

  const {
    layout: {
      rightbar: { isOpen: rightbarIsOpen },
      sidebar: { isOpen: sidebarIsOpen },
    },
  } = useAppSelector((state) => state);

  const theme = useTheme();
  const isSm = useMobile();
  const isMd = useMobile("md");
  const isLg = useMobile("lg");
  const isXl = useMobile("xl");

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) =>
        prevProgress >= avaliacao.percentual
          ? avaliacao.percentual
          : prevProgress + 1
      );
    }, 50);

    return () => {
      clearInterval(timer);
    };
  }, []);

  let gridSize: GridSize = 12;
  if (isSm) gridSize = 8;
  else if (isMd) gridSize = 6;
  else if (isLg && sidebarIsOpen && rightbarIsOpen) gridSize = 10;
  else if (isLg && (sidebarIsOpen || rightbarIsOpen)) gridSize = 6;
  else if (isLg && !sidebarIsOpen && !rightbarIsOpen) gridSize = 4;
  else if (isXl && sidebarIsOpen && rightbarIsOpen) gridSize = 4;
  else if (isXl && (sidebarIsOpen || rightbarIsOpen)) gridSize = 3;
  else if (isXl && !sidebarIsOpen && !rightbarIsOpen) gridSize = 2;
  else if (sidebarIsOpen && rightbarIsOpen) gridSize = 4;
  else if (sidebarIsOpen || rightbarIsOpen) gridSize = 3;
  else if (!sidebarIsOpen && !rightbarIsOpen) gridSize = 2;

  let title = "Você ainda não avaliou este aluno(a)";
  if (avaliacao.idAvaliacao) {
    title = `Você respondeu ${avaliacao.qtdeRespondida} de ${avaliacao.qtdePerguntas} perguntas`;
  }

  return (
    <Grid item xs={gridSize}>
      <Card variant="outlined" sx={{ height: "100%" }}>
        <CardHeader
          title={title}
          titleTypographyProps={{
            variant: "body2",
          }}
          sx={{ pt: 0.5, pb: 0.5, pr: 1 }}
          action={
            <Tooltip
              title={avaliacao.idAvaliacao ? "Remover avaliação" : title}
            >
              <span>
                <IconButton
                  size="small"
                  color="secondary"
                  disabled={!avaliacao.idAvaliacao}
                  onClick={() => handleDelete(avaliacao)}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          }
        />
        <Tooltip
          title={`Clique para avaliar o(a) aluno(a) ${avaliacao.nomeAluno}`}
        >
          <CardActionArea
            onClick={() => handleClick(avaliacao)}
            sx={{
              height: `calc(100% - ${theme.spacing(10)})`,
              "& .MuiCardContent-root:hover": {
                filter: !avaliacao.idAvaliacao
                  ? "grayscale(0) !important"
                  : undefined,
              },
            }}
          >
            <CardContent
              sx={{
                height: "100%",
                filter: !avaliacao.idAvaliacao ? "grayscale(1)" : undefined,
              }}
            >
              <Grid container>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      displayName={avaliacao.nomeAluno}
                      src={
                        avaliacao.photoUrlAluno ||
                        getRandomChildren(avaliacao.sexoAluno)
                      }
                      sx={{
                        minWidth: theme.spacing(15),
                        minHeight: theme.spacing(15),
                        fontSize: theme.typography.h3.fontSize,
                        margin: "auto",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        right: theme.spacing(10),
                        bottom: theme.spacing(-0.5),
                        width: theme.spacing(3.5),
                        height: theme.spacing(3.5),
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        color={progress < 80 ? "secondary" : "success"}
                        value={progress}
                        sx={{
                          width: theme.spacing(3.5).concat(" !important"),
                          height: theme.spacing(3.5).concat(" !important"),
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                        }}
                      />
                      {progress < 100 && (
                        <Typography
                          variant="caption"
                          component="p"
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            lineHeight: theme.spacing(3).concat(" !important"),
                            width: theme.spacing(3.5),
                            textAlign: "center",
                          }}
                        >
                          {progress.toFixed(0).concat("%")}
                        </Typography>
                      )}
                      {progress == 100 && (
                        <CheckIcon
                          color="success"
                          sx={{
                            position: "absolute",
                            bottom: "2px",
                            right: 0,
                            lineHeight: theme.spacing(3).concat(" !important"),
                            width: theme.spacing(3.5),
                            textAlign: "center",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    variant="h5"
                    color={theme.palette.primary.main}
                    noWrap
                  >
                    {avaliacao.nomeAluno.rUpper()}
                  </Typography>
                  <Typography variant="body2">
                    Nº chamada: <b>{avaliacao.nrChamada}</b>
                  </Typography>
                  <Typography variant="body2">
                    Código: <b>{avaliacao.codigoAluno}</b>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CardActionArea>
        </Tooltip>
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <ButtonGroup size="small">
            <Tooltip title="Ver todas as avaliações deste aluno">
              <Button
                startIcon={<FilterIcon />}
                onClick={() => handleAvaliacoes(avaliacao)}
              >
                Avaliações
              </Button>
            </Tooltip>

            <Button
              startIcon={<ViewIcon />}
              disabled={!avaliacao.idAvaliacao}
              onClick={() => handleVisualizar(avaliacao)}
            >
              <Tooltip title="Visualizar avaliação do aluno">
                <>Visualizar</>
              </Tooltip>
            </Button>

            <Tooltip title="Responder avaliação">
              <Button
                startIcon={<StarIcon />}
                onClick={() => handleClick(avaliacao)}
              >
                Avaliar
              </Button>
            </Tooltip>
          </ButtonGroup>
        </CardActions>
      </Card>
    </Grid>
  );
};
